<template>
  <nuxt-link
    :active-class="!onlyExactActive ? 'font-bold text-black' : ''"
    exact-active-class="font-bold text-black"
    class="whitespace-no-wrap group inline-flex items-center justify-center gap-1 p-3 transition duration-100 ease-linear focus:ring-2 disabled:opacity-30"
    :to="to"
    :class="linkClasses"
  >
    <Icon
      v-if="icon"
      aria-hidden="true"
      focusable="false"
      :class="textColorClasses"
      :icon="icon"
      class="size-4"
    />
    <slot />
  </nuxt-link>
</template>

<script lang="ts">
import Icon from '../../../components/ui/icons/Icon.vue'

export default defineComponent({
  name: 'LinkAsButton',
  components: {
    Icon,
  },
  props: {
    to: {
      type: [String, Object] as PropType<string | object>,
      required: true,
    },
    icon: {
      type: String as PropType<string>,
      default: '',
    },
    onlyExactActive: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    type: {
      type: String as PropType<string>,
      default: 'primary',
      validator: (val: string) =>
        ['primary', 'secondary', 'tertiary', 'ghost'].includes(val),
    },
    isUppercase: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
  },
  setup(props) {
    const isPrimary = computed(() => props.type === 'primary')
    const isSecondary = computed(() => props.type === 'secondary')
    const isTertiary = computed(() => props.type === 'tertiary')
    const isGhost = computed(() => props.type === 'ghost')
    const linkClasses = computed(() => {
      const baseClasses = 'border font-semibold'
      let perTypeClasses = ''
      let hoverClasses = ''
      let focusClasses = ''
      if (isPrimary.value) {
        perTypeClasses = 'bg-primary border-primary text-white'
        hoverClasses = 'hover:bg-primary-400'
        focusClasses = 'focus:bg-primary-400 focus:ring-primary focus:ring-1'
      }
      if (isSecondary.value) {
        perTypeClasses = 'bg-secondary text-primary'
        hoverClasses = 'hover:bg-secondary-600 hover:text-primary-400'
        focusClasses =
          'focus:bg-secondary-600 focus:ring-secondary focus:ring-1'
      }
      if (isTertiary.value) {
        perTypeClasses = 'bg-transparent text-primary border-primary'
        hoverClasses = 'hover:bg-secondary-300 hover:text-primary-400'
        focusClasses = 'focus:bg-secondary-300 focus:ring-primary focus:ring-1'
      }
      if (isGhost.value) {
        perTypeClasses = 'text-primary hover:text-primary-600 px-0'
      }
      return `${baseClasses} ${
        props.isUppercase ? 'uppercase' : ''
      } ${perTypeClasses} ${hoverClasses} ${focusClasses}`
    })
    return {
      isPrimary,
      isSecondary,
      isTertiary,
      isGhost,
      textColorClasses: computed(() => ({
        'text-white': isPrimary.value,
        'text-primary-100': isSecondary.value,
        'text-primary': isTertiary.value || isGhost.value,
      })),
      linkClasses,
    }
  },
})
</script>
